import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { applyAppearance, resetAppearance } from "@/lib/appearance";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = anonymous, object = logged-in
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("ft_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Item 10: apply saved appearance globally (light overrides ThemeContext)
      applyAppearance(data.appearance_theme || "dark", data.appearance_font || "Manrope");
    } catch {
      localStorage.removeItem("ft_token");
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) return data;
    localStorage.setItem("ft_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const mfaVerify = async (tmp_token, code) => {
    const { data } = await api.post("/auth/mfa/verify", { tmp_token, code });
    localStorage.setItem("ft_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    if (data.token) {
      localStorage.setItem("ft_token", data.token);
      setUser(data.user);
    }
    return data;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    localStorage.setItem("ft_token", data.token);
    setUser(data.user);
    return data;
  };

  const acceptInvite = async (payload) => {
    const { data } = await api.post("/invitations/accept", payload);
    localStorage.setItem("ft_token", data.token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    localStorage.removeItem("ft_token");
    resetAppearance();
    setUser(false);
  };

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const hasPerm = useCallback((perm) => {
    if (!user || user === false) return false;
    if (user.is_super_admin || user.is_admin) return true;
    return Array.isArray(user.permissions) && user.permissions.includes(perm);
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, login, mfaVerify, register, signup, acceptInvite, logout, formatApiError, refreshMe, hasPerm }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
